import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { trackEvent } from '@/Support/MixpanelTracking';
import ReportTemplateData = App.Data.ReportTemplate.ReportTemplateData;
import ReportData = App.Data.Report.ReportData;
import {
  type OrderableSubTabs, type OrderableTabs, type ReportSubSection, type ReportTab, useReportStore,
} from '@/Stores/Report';

// eslint-disable-next-line import/prefer-default-export
export const useReportEditStore = defineStore('report-edit', () => {
  const page = usePage();

  const reportStore = useReportStore();

  type ReportSection = {
    id: string,
    name: string,
  };

  type ParentReportSection = ReportSection & {
    subsections: ReportSection[],
  };

  type ReportEditState = {
    displayDrawer: boolean;
    reportTemplates: ReportTemplateData[];
    newTemplateName: string;
    selectedTemplateId: string | null;
    sections: ParentReportSection[];
    emptySubSectionsToHide: string[];
  };

  const state = reactive<ReportEditState>({
    displayDrawer: false,
    selectedTemplateId: null,
    newTemplateName: '',
    sections: [],
    reportTemplates: [],
    emptySubSectionsToHide: [
      'pastListingImages',
    ],
  });

  // Computed
  const reportTemplates = computed(() => state.reportTemplates);

  const customTemplates = computed(() => state.reportTemplates.filter((reportTemplate) => !reportTemplate.isSystem));

  const selectedTemplate = computed(() => reportTemplates.value.find((reportTemplate) => reportTemplate.id === state.selectedTemplateId));

  const sections = computed(() => state.sections);

  const displayDrawer = computed(() => state.displayDrawer);

  const newTemplateName = computed(() => state.newTemplateName);

  const emptySubSectionsToHide = computed(() => state.emptySubSectionsToHide);

  // Functions
  const changeSelectedTemplate = (reportTemplate: ReportTemplateData) => {
    state.selectedTemplateId = reportTemplate.id;

    trackEvent('Property Report', {
      Status: 'Template changed',
      'Property ID': reportStore.propertyId,
      'Template Name': reportTemplate.name,
      Account: reportStore.accountId,
      Company: page.props.auth.user?.company?.name ?? null,
    });

    state.sections = reportTemplate.content.sections;
    reportStore.templateChanged(reportTemplate);
  };

  const openDrawer = () => {
    state.displayDrawer = true;
  };

  const closeDrawer = () => {
    state.displayDrawer = false;
  };

  const filterSubSections = (tab: ParentReportSection) => tab.subsections.filter((section) => {
    if (!emptySubSectionsToHide.value.includes(section.id)) {
      return true;
    }

    const subSection = reportStore.content[tab.id as keyof OrderableTabs][section.id as keyof OrderableSubTabs] as ReportSubSection;

    if ('isEmpty' in subSection) {
      return !subSection.isEmpty;
    }

    return true;
  });

  /**
   * This method sets the "order" property value for each section based off the
   * "order" property value from the Report store. This ensures that the
   * order which has been saved with a Report is correct for the sections.
   */
  const setOrderFromSavedReport = (report: ReportData) => {
    state.sections = report.reportTemplate?.content.sections.map((section) => ({
      ...section,
      subsections: filterSubSections(section).map((subSection) => ({
        ...subSection,
        order: (reportStore.content[section.id as keyof OrderableTabs][subSection.id as keyof OrderableSubTabs] as ReportSubSection).order,
      })).sort((a, b) => (a.order - b.order)),
      order: reportStore.content[section.id as keyof OrderableTabs].order,
    })).sort((a, b) => (a.order - b.order)) ?? [];
  };

  const newTemplateErrors = ref({
    name: '',
  });

  const getSectionDisplayState = (section: ParentReportSection, subsection?: ReportSection) => {
    if (subsection) {
      return (reportStore.content[section.id as keyof OrderableTabs][subsection.id as keyof OrderableSubTabs] as ReportSubSection).display;
    }

    return (reportStore.content[section.id as keyof OrderableTabs] as ReportTab).display;
  };

  const getSubSectionContent = (subsection: ReportSection) => {
    if (subsection.id === 'propertyValue') {
      const propertyValueContent = reportStore.content.overview.propertyValue;

      return {
        estimatedValueLabel: propertyValueContent.estimatedValueLabel,
        showEstimatedValue: propertyValueContent.showEstimatedValue,
        showEstimatedRange: propertyValueContent.showEstimatedRange,
        estimatedRentalValueLabel: propertyValueContent.estimatedRentalValueLabel,
        showEstimatedRentalValue: propertyValueContent.showEstimatedRentalValue,
        showAnnualRentalYield: propertyValueContent.showAnnualRentalYield,
      };
    }

    return null;
  };

  const getSubSections = (section: ParentReportSection) => section.subsections.map((subsection, index) => ({
    id: subsection.id,
    display: getSectionDisplayState(section, subsection),
    order: index + 1,
    content: getSubSectionContent(subsection),
  }));

  const setTemplates = (templates: ReportTemplateData[]) => {
    state.reportTemplates = templates;
  };

  const saveTemplate = async (onSuccess: () => void) => {
    const templateSections = state.sections.map((section, index) => ({
      id: section.id,
      display: getSectionDisplayState(section),
      order: index + 1,
      subsections: getSubSections(section),
    }));

    const template = {
      sections: templateSections,
      settings: {
        anonymiseAddress: reportStore.settings.anonymiseAddress,
        hideEmptySections: reportStore.settings.hideEmptySections,
        hideContactDetails: reportStore.settings.hideContactDetails,
      },
    };

    router.post(route('report-template.store', { company: page.props.auth.user?.company }), {
      reportId: reportStore.reportId,
      name: newTemplateName.value,
      template,
    }, {
      only: ['reportTemplates'],
      onSuccess: () => {
        onSuccess();
        trackEvent('Property Report', {
          Status: 'Template created',
          'Property ID': reportStore.propertyId,
          'Template Name': selectedTemplate.value?.name ?? null,
          Account: reportStore.accountId,
          Company: page.props.auth.user?.company?.name ?? null,
        });

        reportStore.setPreserveState(true);
        setTemplates(page.props.reportTemplates as ReportTemplateData[]);
        changeSelectedTemplate(reportTemplates.value.find((reportTemplate) => reportTemplate.name === state.newTemplateName) as ReportTemplateData);

        reportStore.setPreserveState(false);
        state.newTemplateName = '';
      },
      onError: () => {
        newTemplateErrors.value.name = page.props.errors.name;
      },
    });
  };

  const updateTemplate = async (templateId: string) => {
    const templateSections = state.sections.map((section, index) => ({
      id: section.id,
      display: getSectionDisplayState(section),
      order: index + 1,
      subsections: getSubSections(section),
    }));

    const template = {
      sections: templateSections,
      settings: {
        anonymiseAddress: reportStore.settings.anonymiseAddress,
        hideEmptySections: reportStore.settings.hideEmptySections,
        hideContactDetails: reportStore.settings.hideContactDetails,
      },
    };

    router.put(route('report-template.update', { reportTemplate: templateId }), {
      reportId: reportStore.reportId,
      id: templateId,
      name: newTemplateName.value,
      template,
    }, {
      onSuccess: () => {
        setTemplates(page.props.reportTemplates as ReportTemplateData[]);
        changeSelectedTemplate(reportTemplates.value.find((reportTemplate) => reportTemplate.id === templateId) as ReportTemplateData);
      },
      onError: () => {
        newTemplateErrors.value.name = page.props.errors.name;
      },
    });
  };

  const deleteTemplate = (templateId: string) => {
    const destroy = () => {
      router.delete(route('report-template.destroy', { reportTemplate: templateId }), {
        onSuccess: () => {
          setTemplates(page.props.reportTemplates as ReportTemplateData[]);
        },
      });
    };

    if (reportStore.templateId === templateId) {
      reportStore.templateChanged(reportTemplates.value.find((reportTemplate) => reportTemplate.name === 'Property report') as ReportTemplateData, () => destroy());
      return;
    }

    destroy();
  };

  return {
    closeDrawer,
    customTemplates,
    deleteTemplate,
    displayDrawer,
    filterSubSections,
    newTemplateErrors,
    newTemplateName,
    openDrawer,
    reportTemplates,
    saveTemplate,
    sections,
    selectedTemplate,
    setOrderFromSavedReport,
    setTemplates,
    state,
    changeSelectedTemplate,
    updateTemplate,
  };
});
